const formatPrice = (num) => parseFloat(Number(num).toFixed(3)).toString();

const STATIC_SERVICES = [
  { id: "s1", name: "Home Inspection", description: "Thorough evaluation of the property's condition", price: 350, duration: 2.5 },
  { id: "s2", name: "Termite Inspection", description: "Check for wood-destroying insects and damage", price: 150, duration: 1 },
  { id: "s3", name: "Radon Testing", description: "Test for harmful radon gas levels", price: 175, duration: 0.75 },
  { id: "s4", name: "Mold Inspection", description: "Identify presence of harmful mold and moisture issues", price: 200, duration: 1.5 },
  { id: "s5", name: "Sewer Scope", description: "Camera inspection of sewer line for damage or blockages", price: 225, duration: 1 },
];

// Format minutes into a readable duration string
const formatDuration = (minutes) => {
  if (!minutes) return "";
  const hrs = minutes / 60;
  if (minutes < 60) return `${minutes} min`;
  if (minutes % 60 === 0) return `${hrs} ${hrs === 1 ? "hour" : "hours"}`;
  const h = Math.floor(hrs);
  const m = minutes % 60;
  return `${h}h ${m}m`;
};

const SelectServicesForm = ({ formData, handleFormChange, validationErrors, apiServices = [] }) => {
  // Map API response shape → internal shape; fall back to static list
  const services = apiServices.length > 0
    ? apiServices
        .slice()
        .sort((a, b) => (a.ordinal ?? 0) - (b.ordinal ?? 0))
        .map((s) => ({
          id: s.id,
          name: s.name,
          description: s.description || "",
          price: s.amount ?? 0,
          durationMinutes: s.minutes ?? 0,
        }))
    : STATIC_SERVICES.map((s) => ({ ...s, durationMinutes: s.duration * 60 }));

  const handleCheck = (service, checked) => {
    // Clear date/time selections whenever services change
    handleFormChange("appointmentTime", "");
    handleFormChange("preferredInspector", "any");
    handleFormChange("availableInspectors", []);

    const sid = String(service.id);
    if (checked) {
      handleFormChange("services", [...formData.services, sid]);
      handleFormChange("servicePrices", { ...formData.servicePrices, [sid]: service.price });
      handleFormChange("serviceDurations", { ...formData.serviceDurations, [sid]: service.durationMinutes });
      handleFormChange("serviceNames", { ...formData.serviceNames, [sid]: service.name });
    } else {
      handleFormChange("services", formData.services.filter((s) => s !== sid));
      const prices = { ...formData.servicePrices };
      delete prices[sid];
      handleFormChange("servicePrices", prices);
      const durations = { ...formData.serviceDurations };
      delete durations[sid];
      handleFormChange("serviceDurations", durations);
      const names = { ...formData.serviceNames };
      delete names[sid];
      handleFormChange("serviceNames", names);
    }
  };

  return (
    <div className="p-6 py-0">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">What services do you need?</h2>
      {validationErrors.services && (
        <p className="mb-3 p-2 bg-amber-50 rounded-md text-[#ec2506] text-xs">
          {validationErrors.services}
        </p>
      )}
      <div className="space-y-3">
        {services.map((service) => {
          const checkId = `service-${service.id}`;
          const isChecked = formData.services.includes(String(service.id));
          return (
            <div
              key={service.id}
              className={`flex items-center p-3 border rounded-md bg-white hover:bg-gray-50 transition-colors cursor-pointer ${
                isChecked ? "border-[#239D80] bg-[#239D80]/5" : ""
              }`}
              onClick={() => handleCheck(service, !isChecked)}
            >
              <input
                type="checkbox"
                id={checkId}
                className="h-5 w-5 rounded border-gray-300 text-destructive focus:ring-destructive shrink-0"
                checked={isChecked}
                onChange={(e) => handleCheck(service, e.target.checked)}
                onClick={(e) => e.stopPropagation()}
              />
              <label
                htmlFor={checkId}
                className={`ml-3 flex-1 flex justify-between cursor-pointer ${!service.description ? "items-center" : "items-start"}`}
                onClick={(e) => e.stopPropagation()}
              >
                <div className={!service.description ? "flex items-center" : ""}>
                  <span className="text-sm font-medium text-gray-900">{service.name}</span>
                  {service.description && (
                    <p className="text-xs text-gray-500">{service.description}</p>
                  )}
                </div>
                <div className="text-right ml-4 shrink-0">
                  {service.price > 0 && (
                    <span className="text-sm font-medium text-gray-900">${formatPrice(service.price)}</span>
                  )}
                  {service.durationMinutes > 0 && (
                    <p className="text-xs text-gray-500">{formatDuration(service.durationMinutes)}</p>
                  )}
                </div>
              </label>
            </div>
          );
        })}
      </div>
      <div className="mt-6">
        <label htmlFor="additionalNotes" className="block text-sm font-medium text-gray-700 mb-1">
          Additional Notes (Optional)
        </label>
        <textarea
          id="additionalNotes"
          value={formData.additionalNotes}
          onChange={(e) => handleFormChange("additionalNotes", e.target.value)}
          placeholder="Any special instructions or information about the property..."
          className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 h-24 my-[10px] resize-y"
        />
      </div>
    </div>
  );
};
export default SelectServicesForm;
